import {useState} from "react";
import {Button, Form, ListGroup} from "react-bootstrap";
import {UsersApi} from "../../api/UsersApi";
import {UserNoteViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {formatDateTime} from "../../components/format";

const MAX_LENGTH = 4000;

// Administrators' notes on a user's account (the user never sees them). Adding and deleting happen at once. No form
// element: it's also shown inside the edit user modal's form.
export function UserNotes({username}: { username: string }) {
    const {data: notes, error: loadError, loading, setData} = useAsync(() => UsersApi.getNotes(username), [username]);
    const [text, setText] = useState('');
    const [deleting, setDeleting] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const run = async (action: () => Promise<(current: UserNoteViewModel[]) => UserNoteViewModel[]>) => {
        setBusy(true);
        setError(undefined);
        try {
            const update = await action();
            setData(current => update(current ?? []));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const add = () => run(async () => {
        const note = await UsersApi.addNote(username, text.trim());
        setText('');
        return current => [note, ...current];
    });

    const remove = (id: string) => run(async () => {
        await UsersApi.deleteNote(username, id);
        setDeleting(undefined);
        return current => current.filter(x => x.id !== id);
    });

    return (
        <div>
            <ErrorAlert error={loadError}/>
            <ErrorAlert error={error} shownFields={['text']} messages={{ 404: 'That note was already deleted.' }} onClose={() => setError(undefined)}/>
            <Form.Group className="mb-2" controlId={`user-notes-${username}`}>
                <Form.Label className="visually-hidden">New note</Form.Label>
                <Form.Control as="textarea" rows={3} maxLength={MAX_LENGTH} placeholder="Add a note…" value={text}
                              isInvalid={!!fieldError(error, 'text')} onChange={e => setText(e.target.value)}/>
                <Form.Control.Feedback type="invalid">{fieldError(error, 'text')}</Form.Control.Feedback>
                <Form.Text>Private to administrators.</Form.Text>
            </Form.Group>
            <Button size="sm" variant="outline-primary" className="mb-3" disabled={busy || !text.trim()} onClick={add}>Add note</Button>
            {loading && !notes && <LoadingSpinner/>}
            {notes && notes.length === 0 && <p className="text-body-secondary small mb-0">No notes yet.</p>}
            {notes && notes.length > 0 && (
                <ListGroup as="ul" variant="flush" aria-label="Notes">
                    {notes.map(note => (
                        <ListGroup.Item as="li" key={note.id} className="px-0">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                                <div className="small text-body-secondary">{note.createdBy}, {formatDateTime(note.createdOn)}</div>
                                {deleting === note.id ? (
                                    <span className="text-nowrap">
                                        <Button size="sm" variant="danger" className="me-1" disabled={busy} onClick={() => remove(note.id)}>Delete</Button>
                                        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setDeleting(undefined)}>Keep</Button>
                                    </span>
                                ) : (
                                    <Button size="sm" variant="link" className="p-0 text-danger" disabled={busy}
                                            onClick={() => setDeleting(note.id)}>Delete…</Button>
                                )}
                            </div>
                            <div className="user-note">{note.text}</div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </div>
    );
}
